
import React, { useState, useEffect } from 'react';
import {
  getBookings,
  getBookingById,
  updateBooking,
  updateBookingStatus
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import { useSelector } from 'react-redux';

/* ============================================================
 * MODERN UI PRIMITIVES (mirror PrimaryRouteManage.css)
 * ============================================================ */
const ICON_PATHS = {
    route: 'M5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M19 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M7 17h7a3 3 0 0 0 0-6h-4a3 3 0 0 1 0-6h7',
    check: 'M22 11.1V12a10 10 0 1 1-5.9-9.1 M22 4 12 14l-3-3',
    calendar: 'M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    pin: 'M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
    stop: 'M12 3v18 M5 6h11l3 3-3 3H5z',
    search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.3-4.3',
    eye: 'M2 12s4-8 10-8 10 8 10 8-4 8-10 8S2 12 2 12z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    trash: 'M3 6h18 M8 6V4h8v2 M6 6l1 14h10l1-14 M10 11v6 M14 11v6',
    grid: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
    list: 'M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01',
    tag: 'M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z M7.5 7.5h.01',
    plus: 'M12 5v14 M5 12h14',
    map: 'M1 6l7-3 8 3 7-3v15l-7 3-8-3-7 3z M8 3v15 M16 6v15',
    bus: 'M5 3h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M3 11h18 M7 21v-3 M17 21v-3 M7 15h.01 M17 15h.01',
    edit: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
    info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 16v-4 M12 8h.01',
    refresh: 'M23 4v6h-6 M1 20v-6h6 M3.5 9a9 9 0 0 1 14.9-3.4L23 10 M1 14l4.6 4.4A9 9 0 0 0 20.5 15',
    x: 'M18 6 6 18 M6 6l12 12'
};

const Icon = ({ name, size = 18 }) => (
    <svg className="prm-ico" viewBox="0 0 24 24" width={size} height={size} fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={ICON_PATHS[name]} />
    </svg>
);

const BusSvg = ({ size = 34 }) => (
    <svg viewBox="0 0 64 34" width={size} height={(size * 34) / 64} aria-hidden="true">
        <rect x="2" y="3" width="58" height="23" rx="7" fill="#f59e0b" />
        <rect x="2" y="19" width="58" height="4" fill="#d97706" />
        <rect x="8" y="8" width="8" height="8" rx="2" fill="#0b1f44" />
        <rect x="19" y="8" width="8" height="8" rx="2" fill="#0b1f44" />
        <rect x="30" y="8" width="8" height="8" rx="2" fill="#0b1f44" />
        <path d="M43 8h10a4 4 0 0 1 4 4v4H43z" fill="#0b1f44" />
        <rect x="57" y="20" width="4" height="3" rx="1" fill="#fff7d6" />
        <circle cx="17" cy="27" r="5.5" fill="#0b1f44" /><circle cx="17" cy="27" r="2.2" fill="#cbd5e1" />
        <circle cx="46" cy="27" r="5.5" fill="#0b1f44" /><circle cx="46" cy="27" r="2.2" fill="#cbd5e1" />
    </svg>
);

const useCountUp = (target, duration = 900) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        let raf, start;
        const step = (t) => {
            if (!start) start = t;
            const p = Math.min((t - start) / duration, 1);
            setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return value;
};

const StatCard = ({ icon, label, value, hint, tone, delay = 0 }) => {
    const shown = useCountUp(value);
    return (
        <div className={`prm-stat tone-${tone}`} style={{ animationDelay: `${delay}ms` }}>
            <div className="prm-stat-icon"><Icon name={icon} size={24} /></div>
            <div className="prm-stat-body">
                <div className="prm-stat-label">{label}</div>
                <div className="prm-stat-value">{shown}</div>
                <div className="prm-stat-hint">{hint}</div>
            </div>
        </div>
    );
};

const Switch = ({ checked, onChange, title }) => (
    <label className="prm-switch" title={title}>
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span />
    </label>
);

const FormSection = ({ n, title, hint, children, delay = 0 }) => (
    <section className="prm-fsec" style={{ animationDelay: `${delay}ms` }}>
        <header className="prm-fsec-head">
            <span className="prm-fsec-num">{n}</span>
            <div>
                <h4>{title}</h4>
                {hint && <p>{hint}</p>}
            </div>
        </header>
        <div className="prm-fsec-body">{children}</div>
    </section>
);

const Field = ({ label, required, children, full, note }) => (
    <div className={`prm-field ${full ? 'full' : ''}`}>
        {label && <label className="prm-label">{label}{required && <em>*</em>}</label>}
        {children}
        {note && <small className="prm-note">{note}</small>}
    </div>
);

const PrmCheckbox = ({ label, checked, onChange }) => (
    <label className={`prm-check ${checked ? 'on' : ''}`}>
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="box"><Icon name="check" size={12} /></span>
        <span className="txt">{label}</span>
    </label>
);


const formatDateDMY = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatTime = (timeString) => {
  if (!timeString)
    return 'N/A';
  return timeString.substring(0, 5);
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>PNR</th>
          <th>Passenger</th>
          <th>Route</th>
          <th>Bus</th>
          <th>Booking Date</th>
          <th>Booking Time</th>
          <th>Journey Date</th>
          <th>Journey Time</th>
          <th>Seats</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Payment</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal" style={{ maxWidth: '800px' }}>
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-detail-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            ))}
          </div>
          <div>
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-detail-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const BookingManage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingBooking, setViewingBooking] = useState(null);
  const [error, setError] = useState(null);
  const { token } = useSelector(state => state.user);

  const [searching, setSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    pnr: '',
    passengerName: '',
    passengerEmail: '',
    passengerPhone: '',
    route: '',
    bus: '',
    bookingDateFrom: '',
    bookingDateTo: '',
    journeyDateFrom: '',
    journeyDateTo: '',
    minAmount: '',
    maxAmount: '',
    paymentStatus: '',
    paymentMethod: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBookings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, statusFilter, filters]);

  const fetchBookings = async (isInitialLoad = false) => {
  try {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setSearching(true);
    }

    const filterParams = {};

    if (searchTerm) {
      filterParams.search = searchTerm;
    }

    if (statusFilter && statusFilter !== 'All Status') {
      filterParams.status = statusFilter;
    }

    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParams[key] = filters[key];
      }
    });

    const params = {
      page: currentPage,
      limit: pageSize,
      ...filterParams
    };

    const response = await getBookings(token, params);
    if (response && response.success) {
      const formattedBookings = response.bookings.map(booking => ({
        id: booking.id,
        pnr: booking.pnr,
        user: booking.user?.name || 'N/A',
        userEmail: booking.user?.email || 'N/A',
        userPhone: booking.user?.phone || 'N/A',
        route: booking.route?.from && booking.route?.to
          ? `${booking.route.from} → ${booking.route.to}`
          : 'N/A',
        bus: booking.bus?.name || 'N/A',
        busNumber: booking.bus?.number || 'N/A',
        bookingDate: booking.bookedOn || booking.createdAt,
        bookingDateTime: booking.createdAt || booking.bookedOn,
        journeyDate: booking.journeyDetails?.date || booking.travelDate,
        journeyTime: booking.journeyDetails?.time || booking.travelTime,
        seats: booking.seatsCount || 0,
        seatNumbers: booking.seats?.map(s => s.seatNumber).join(', ') || 'N/A',
        amount: booking.amount || 0,
        status: booking.status || 'Pending',
        payment: booking.paymentStatus || 'Pending',
        paymentMethod: booking.paymentMethod || 'N/A',
        boardingPoint: booking.boardingPoint || {},
        droppingPoint: booking.droppingPoint || {},
        isInstantBooking: booking.isInstantBooking || false,
        rawData: booking
      }));

      setBookings(formattedBookings);
      setTotalPages(response.totalPages || 1);
      setTotalBookings(response.total || 0);

      const hasActiveFilters =
        statusFilter !== 'All Status' ||
        searchTerm !== '' ||
        Object.values(filters).some(value => value !== '');
      setIsFiltered(hasActiveFilters);
    } else {
      setError('Failed to fetch bookings.');
      setBookings([]);
    }

  } catch (err) {
    console.error('Error fetching bookings:', err);
    setError('Failed to fetch bookings. Please try again.');
    setBookings([]);
  } finally {
    setLoading(false);
    setSearching(false);
  }
};

  const fetchBookingById = async (id) => {
    try {
      const response = await getBookingById(id, token);
      return response.data || response;
    } catch (err) {
      console.error('Error fetching booking:', err);
      throw err;
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchBookings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      pnr: '',
      passengerName: '',
      passengerEmail: '',
      passengerPhone: '',
      route: '',
      bus: '',
      bookingDateFrom: '',
      bookingDateTo: '',
      journeyDateFrom: '',
      journeyDateTo: '',
      minAmount: '',
      maxAmount: '',
      paymentStatus: '',
      paymentMethod: ''
    });
    setSearchTerm('');
    setStatusFilter('All Status');
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const getSafeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const getSafeLowerCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(value);
  };

  const handleExportCSV = () => {
    if (bookings.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['PNR', 'Passenger', 'Email', 'Phone', 'Route', 'Bus', 'Booking Date', 'Booking Time', 'Journey Date', 'Journey Time', 'Seats', 'Amount', 'Status', 'Payment Status'];
    const csvData = bookings.map(booking => [
      booking.pnr || '',
      booking.user || '',
      booking.userEmail || '',
      booking.userPhone || '',
      booking.route || '',
      booking.bus || '',
      formatDateDMY(booking.bookingDate),
      formatTime(booking.bookingDateTime),
      formatDateDMY(booking.journeyDate),
      booking.journeyTime || '',
      booking.seats || 0,
      booking.amount || 0,
      booking.status || '',
      booking.payment || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleViewBooking = async (booking) => {
    try {
      setIsFetchingDetails(true);
      const bookingData = await fetchBookingById(booking.id);

      const rawBooking = booking.rawData || booking;

      setViewingBooking({
        ...booking,
        name: booking.user || 'N/A',
        email: booking.userEmail || 'N/A',
        phone: booking.userPhone || 'N/A',
        status: booking.status || 'Pending',
        paymentStatus: booking.payment || 'Pending',
        route: booking.route || 'N/A',
        routeFrom: rawBooking.rawData?.route?.from || 'N/A',
        routeTo: rawBooking.rawData?.route?.to || 'N/A',
        bus: booking.bus || 'N/A',
        busNumber: booking.busNumber || 'N/A',
        bookingDate: booking.bookingDate,
        bookingDateTime: booking.bookingDateTime,
        journeyDate: booking.journeyDate,
        journeyTime: booking.journeyTime,
        seats: booking.seats || 0,
        seatNumbers: booking.seatNumbers || 'N/A',
        amount: booking.amount || 0,
        paymentMethod: booking.paymentMethod || 'N/A',
        boardingPoint: booking.boardingPoint || {},
        droppingPoint: booking.droppingPoint || {},
        passengers: rawBooking.rawData?.seats?.map(seat => ({
          name: seat.passenger?.fullName || 'N/A',
          age: seat.passenger?.age || 'N/A',
          gender: seat.passenger?.gender || 'N/A',
          seatNumber: seat.seatNumber || 'N/A'
        })) || []
      });
    } catch (err) {
      setViewingBooking(booking);
      console.warn('Could not fetch latest booking details. Showing cached data:', err);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking.id);
    setEditForm({
      status: booking.status || '',
      paymentStatus: booking.payment || '',
      boardingPoint: booking.boardingPoint || {},
      droppingPoint: booking.droppingPoint || {}
    });
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      const updateData = {
        status: editForm.status,
        paymentStatus: editForm.paymentStatus,
        boardingPoint: editForm.boardingPoint,
        droppingPoint: editForm.droppingPoint
      };

      const response = await updateBooking(editingBooking, updateData, token);

      if (response.success) {
        setBookings(prevBookings =>
          prevBookings.map(booking =>
            booking.id === editingBooking ? {
              ...booking,
              status: response.booking?.status || editForm.status || booking.status,
              payment: response.booking?.paymentStatus || editForm.paymentStatus || booking.payment,
              boardingPoint: response.booking?.boardingPoint || editForm.boardingPoint || booking.boardingPoint,
              droppingPoint: response.booking?.droppingPoint || editForm.droppingPoint || booking.droppingPoint
            } : booking
          )
        );

        setEditingBooking(null);
        setEditForm({});
        alert('Booking updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update booking');
      }
    } catch (err) {
      console.error('Error updating booking:', err);
      setError('Failed to update booking. Please try again.');
      alert('Failed to update booking. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingBooking(null);
    setEditForm({});
    setError(null);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      setError(null);

      const response = await updateBookingStatus(bookingId, { status: newStatus }, token);

      if (response.success) {
        setBookings(prevBookings =>
          prevBookings.map(booking =>
            booking.id === bookingId ? {
              ...booking,
              status: newStatus || booking.status
            } : booking
          )
        );

        alert(`Booking status updated to ${newStatus}`);
      } else {
        throw new Error(response.message || 'Failed to update booking status');
      }
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError('Failed to update booking status. Please try again.');
      alert('Failed to update booking status. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeViewModal = () => {
    setViewingBooking(null);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    clearFilters();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const confirmedBookings = bookings.filter(b =>
    getSafeLowerCase(b.status) === 'confirmed'
  ).length;

  const totalRevenue = bookings
    .filter(b =>
      getSafeLowerCase(b.status) === 'confirmed' &&
      getSafeLowerCase(b.payment) === 'success'
    )
    .reduce((sum, booking) => sum + getSafeNumber(booking.amount), 0);

  const pendingBookings = bookings.filter(b =>
    getSafeLowerCase(b.status) === 'pending'
  ).length;

  if (loading && bookings.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="prm-page">
      {/* ============================== HERO ============================== */}
      <div className="prm-hero">
        <div className="prm-hero-road" aria-hidden="true">
          <span className="prm-hero-roadbus"><BusSvg size={46} /></span>
        </div>
        <div className="prm-hero-text">
          <span className="prm-eyebrow"><Icon name="ticket" size={13} /> Booking Insights</span>
          <h1>Booking Management</h1>
          <p>Browse bookings, view passenger details, update status and export reports.</p>
        </div>
        <div className="prm-hero-actions">
          <form onSubmit={handleSearchSubmit} style={{ margin: 0 }}>
            <div className="prm-search">
              <Icon name="search" size={17} />
              <input
                type="text"
                placeholder="Search by PNR or route…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button type="button" className="prm-search-clear" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>×</button>
              )}
            </div>
          </form>
          <button className="prm-btn ghost" style={{ padding: '13px 16px' }} onClick={toggleFilters} title="Filters">
            <Icon name="search" size={16} />
          </button>
          <button className="prm-btn ghost" style={{ padding: '13px 16px' }} onClick={handleRefresh} title="Refresh">
            <Icon name="refresh" size={16} />
          </button>
          <button className="prm-add-btn" onClick={handleExportCSV}>
            <span className="plus"><Icon name="list" size={14} /></span> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="prm-callout rose" style={{ marginTop: 20, background: 'var(--rose-soft)', borderColor: 'var(--rose)', color: '#be123c' }}>
          <Icon name="info" size={18} />
          <div>
            <strong>Error</strong>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* ============================== STATS ============================== */}
      <div className="prm-stats">
        <StatCard icon="ticket" tone="indigo" label="Total Bookings" value={totalBookings || bookings.length} hint="All bookings" delay={80} />
        <StatCard icon="check" tone="green" label="Confirmed" value={confirmedBookings} hint="Confirmed bookings" delay={160} />
        <StatCard icon="wallet" tone="amber" label="Revenue" value={`₹${totalRevenue.toLocaleString()}`} hint="Total revenue" delay={240} />
        <StatCard icon="clock" tone="cyan" label="Pending" value={pendingBookings} hint="Awaiting confirmation" delay={320} />
      </div>

      {/* ============================== FILTER PANEL ============================== */}
      {showFilters && !loading && (
        <div className="prm-fsec" style={{ marginBottom: 22 }}>
          <header className="prm-fsec-head">
            <span className="prm-fsec-num"><Icon name="search" size={16} /></span>
            <div style={{ flex: 1 }}>
              <h4>Filter Bookings</h4>
              <p>Refine the list by booking, passenger, journey or payment details.</p>
            </div>
            <button className="prm-modal-close" style={{ position: 'static' }} onClick={() => setShowFilters(false)}>×</button>
          </header>

          <div className="prm-fsec-body">
            <div className="prm-fgrid">
              <Field label="PNR Number">
                <input type="text" name="pnr" value={filters.pnr} onChange={handleFilterChange} className="prm-input" placeholder="Enter PNR" />
              </Field>
              <Field label="Passenger Name">
                <input type="text" name="passengerName" value={filters.passengerName} onChange={handleFilterChange} className="prm-input" placeholder="Enter passenger name" />
              </Field>
              <Field label="Passenger Email">
                <input type="email" name="passengerEmail" value={filters.passengerEmail} onChange={handleFilterChange} className="prm-input" placeholder="Enter email" />
              </Field>
              <Field label="Passenger Phone">
                <input type="text" name="passengerPhone" value={filters.passengerPhone} onChange={handleFilterChange} className="prm-input" placeholder="Enter phone" />
              </Field>
              <Field label="Route">
                <input type="text" name="route" value={filters.route} onChange={handleFilterChange} className="prm-input" placeholder="Enter route" />
              </Field>
              <Field label="Bus">
                <input type="text" name="bus" value={filters.bus} onChange={handleFilterChange} className="prm-input" placeholder="Enter bus name" />
              </Field>
              <Field label="Booking Status">
                <select value={statusFilter} onChange={handleStatusFilterChange} className="prm-input prm-select">
                  <option value="All Status">All Status</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </Field>
              <Field label="Payment Status">
                <select name="paymentStatus" value={filters.paymentStatus} onChange={handleFilterChange} className="prm-input prm-select">
                  <option value="">All</option>
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </Field>
              <Field label="Booking Date From">
                <input type="date" name="bookingDateFrom" value={filters.bookingDateFrom} onChange={handleFilterChange} className="prm-input" />
              </Field>
              <Field label="Booking Date To">
                <input type="date" name="bookingDateTo" value={filters.bookingDateTo} onChange={handleFilterChange} className="prm-input" />
              </Field>
              <Field label="Journey Date From">
                <input type="date" name="journeyDateFrom" value={filters.journeyDateFrom} onChange={handleFilterChange} className="prm-input" />
              </Field>
              <Field label="Journey Date To">
                <input type="date" name="journeyDateTo" value={filters.journeyDateTo} onChange={handleFilterChange} className="prm-input" />
              </Field>
              <Field label="Min Amount">
                <input type="number" name="minAmount" value={filters.minAmount} onChange={handleFilterChange} className="prm-input" placeholder="Minimum amount" min="0" />
              </Field>
              <Field label="Max Amount">
                <input type="number" name="maxAmount" value={filters.maxAmount} onChange={handleFilterChange} className="prm-input" placeholder="Maximum amount" min="0" />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button className="prm-btn primary" onClick={applyFilters}>
                <Icon name="check" size={15} /> Apply Filters
              </button>
              <button className="prm-btn ghost" onClick={clearFilters}>
                <Icon name="x" size={15} /> Clear All
              </button>
            </div>

            {isFiltered && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16, alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Active Filters:</span>
                {searchTerm && <span className="prm-tag amber">Search: {searchTerm}</span>}
                {statusFilter !== 'All Status' && <span className="prm-tag amber">Status: {statusFilter}</span>}
                {Object.entries(filters).map(([key, value]) =>
                  value && <span key={key} className="prm-tag amber">{key}: {value}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================== TOOLBAR ============================== */}
      <div className="prm-toolbar">
        <div className="prm-toolbar-left">
          <span className="prm-tag navy"><Icon name="ticket" size={12} /> Booking Records</span>
          {isFiltered && <span className="prm-tag amber">Filtered</span>}
        </div>
        <div className="prm-toolbar-right">
          <span className="prm-result-count">Showing <b>{bookings.length}</b> of {totalBookings}</span>
          {searching && <span style={{ color: 'var(--amber-deep)', fontSize: 12 }}>· searching…</span>}
        </div>
      </div>

      {/* ============================== TABLE ============================== */}
      {bookings.length === 0 ? (
        <div className="prm-empty">
          <div className="icon"><Icon name={isFiltered ? 'search' : 'ticket'} size={44} /></div>
          <h3>{isFiltered ? 'No bookings match your filters' : 'No bookings yet'}</h3>
          <p>{isFiltered ? 'Try adjusting your filters or clear them to see all bookings.' : 'Bookings will appear here once customers start booking.'}</p>
          {isFiltered && <button className="prm-btn ghost" onClick={clearFilters}>Clear filters</button>}
        </div>
      ) : (
        <div className="prm-table-wrap">
          <table className="prm-table">
            <thead>
              <tr>
                <th>PNR</th>
                <th>Passenger</th>
                <th>Route</th>
                <th>Bus</th>
                <th>Booking</th>
                <th>Journey</th>
                <th>Seats</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, i) => (
                <tr key={booking.id} style={{ animationDelay: `${i * 40}ms` }}>
                  <td>
                    <span className="pbo-plate">{getSafeString(booking.pnr)}</span>
                  </td>
                  <td>
                    <div className="name">{getSafeString(booking.user)}</div>
                    <div className="sub">{getSafeString(booking.userEmail)}</div>
                  </td>
                  <td>{getSafeString(booking.route)}</td>
                  <td>
                    <div className="name">{getSafeString(booking.bus) || 'N/A'}</div>
                    {booking.busNumber && <div className="sub">({getSafeString(booking.busNumber)})</div>}
                  </td>
                  <td>
                    <div className="name">{formatDateDMY(booking.bookingDate)}</div>
                    <div className="sub">{formatTime(booking.bookingDateTime)}</div>
                  </td>
                  <td>
                    <div className="name">{formatDateDMY(booking.journeyDate)}</div>
                    <div className="sub">{getSafeString(booking.journeyTime)}</div>
                  </td>
                  <td><span className="pbo-count">{getSafeNumber(booking.seats)}</span></td>
                  <td><b>₹{getSafeNumber(booking.amount).toFixed(2)}</b></td>
                  <td>
                    <span className="prm-tag" style={{
                      background: booking.isInstantBooking ? 'var(--amber-soft)' : 'var(--blue-soft)',
                      color: booking.isInstantBooking ? 'var(--amber-deep)' : 'var(--navy-800)'
                    }}>
                      {booking.isInstantBooking ? '⚡ Instant' : '💺 Seat'}
                    </span>
                  </td>
                  <td>
                    {editingBooking === booking.id ? (
                      <select name="status" value={editForm.status || ''} onChange={handleInputChange} className="prm-input prm-select" style={{ padding: '6px 8px', fontSize: 12 }}>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Pending">Pending</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    ) : (
                      <span className={`prm-status ${getSafeLowerCase(booking.status)}`}>
                        {getSafeString(booking.status) || 'Pending'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingBooking === booking.id ? (
                      <select name="paymentStatus" value={editForm.paymentStatus || ''} onChange={handleInputChange} className="prm-input prm-select" style={{ padding: '6px 8px', fontSize: 12 }}>
                        <option value="success">Success</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                      </select>
                    ) : (
                      <span className={`prm-status ${getSafeLowerCase(booking.payment)}`}>
                        {getSafeString(booking.payment) || 'Pending'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingBooking === booking.id ? (
                      <div className="prm-row-actions">
                        <button className="prm-btn primary sm icon-only" onClick={handleSaveEdit} title="Save" aria-label="Save"><Icon name="check" size={14} /></button>
                        <button className="prm-btn ghost sm icon-only" onClick={handleCancelEdit} title="Cancel" aria-label="Cancel"><Icon name="x" size={14} /></button>
                      </div>
                    ) : (
                      <div className="prm-row-actions">
                        <button className="prm-btn view sm icon-only" onClick={() => handleViewBooking(booking)} title="View Details" aria-label="View Details"><Icon name="eye" size={14} /></button>
                        <button className="prm-btn ghost sm icon-only" onClick={() => handleEditBooking(booking)} title="Update Booking" aria-label="Update Booking"><Icon name="edit" size={14} /></button>
                        {getSafeLowerCase(booking.status) === 'pending' && (
                          <button className="prm-btn primary sm icon-only" onClick={() => handleStatusUpdate(booking.id, 'Confirmed')} title="Confirm Booking" aria-label="Confirm Booking"><Icon name="check" size={14} /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ============================== PAGINATION ============================== */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 22, flexWrap: 'wrap' }}>
          <button className="prm-btn ghost" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
          <span style={{ padding: '11px 16px', fontWeight: 700, color: 'var(--navy-800)' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button className="prm-btn ghost" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      {/* ============================== VIEW MODAL ============================== */}
      {viewingBooking && !isFetchingDetails && (
        <div className="prm-overlay" onClick={closeViewModal}>
          <div className="prm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="prm-modal-head">
              <h3>Booking Details — {getSafeString(viewingBooking.pnr)}</h3>
              <p><Icon name="ticket" size={14} /> {getSafeString(viewingBooking.status)} · {getSafeString(viewingBooking.route)}</p>
              <button className="prm-modal-close" onClick={closeViewModal}>×</button>
            </div>

            <div className="prm-modal-body">
              {/* Quick stats */}
              <div className="prm-info-grid">
                <div className="prm-info"><div className="l">Amount</div><div className="v"><Icon name="wallet" size={17} /> ₹{getSafeNumber(viewingBooking.amount)}</div></div>
                <div className="prm-info"><div className="l">Seats</div><div className="v"><Icon name="ticket" size={17} /> {getSafeNumber(viewingBooking.seats)}</div></div>
                <div className="prm-info"><div className="l">Status</div><div className="v"><span className={`prm-status ${getSafeLowerCase(viewingBooking.status)}`}>{getSafeString(viewingBooking.status)}</span></div></div>
                <div className="prm-info"><div className="l">Payment</div><div className="v"><span className={`prm-status ${getSafeLowerCase(viewingBooking.paymentStatus)}`}>{getSafeString(viewingBooking.paymentStatus)}</span></div></div>
              </div>

              {/* Booking info */}
              <div className="prm-section-title">Booking Information</div>
              <div className="prm-info-grid" style={{ marginBottom: 22 }}>
                <div className="prm-info"><div className="l">PNR</div><div className="v">{getSafeString(viewingBooking.pnr)}</div></div>
                <div className="prm-info"><div className="l">Booked On</div><div className="v">{formatDateDMY(viewingBooking.bookingDate)} · {formatTime(viewingBooking.bookingDateTime)}</div></div>
                <div className="prm-info"><div className="l">Payment Method</div><div className="v">{getSafeString(viewingBooking.paymentMethod)}</div></div>
              </div>

              {/* Passenger */}
              <div className="prm-section-title">Passenger Information</div>
              <div className="prm-info-grid" style={{ marginBottom: 22 }}>
                <div className="prm-info"><div className="l">Name</div><div className="v">{getSafeString(viewingBooking.name)}</div></div>
                <div className="prm-info"><div className="l">Email</div><div className="v" style={{ fontSize: 13 }}>{getSafeString(viewingBooking.email)}</div></div>
                <div className="prm-info"><div className="l">Phone</div><div className="v">{getSafeString(viewingBooking.phone)}</div></div>
              </div>

              {/* Journey */}
              <div className="prm-section-title">Journey Details</div>
              <div className="prm-info-grid" style={{ marginBottom: 22 }}>
                <div className="prm-info"><div className="l">Route</div><div className="v">{getSafeString(viewingBooking.route)}</div></div>
                <div className="prm-info"><div className="l">Journey Date</div><div className="v">{formatDateDMY(viewingBooking.journeyDate)}</div></div>
                <div className="prm-info"><div className="l">Departure</div><div className="v">{getSafeString(viewingBooking.journeyTime)}</div></div>
                <div className="prm-info"><div className="l">Bus</div><div className="v">{getSafeString(viewingBooking.bus)}</div></div>
                <div className="prm-info"><div className="l">Bus Number</div><div className="v">{getSafeString(viewingBooking.busNumber)}</div></div>
              </div>

              {/* Boarding / Dropping */}
              {viewingBooking.boardingPoint && viewingBooking.boardingPoint.name && (
                <>
                  <div className="prm-section-title">Boarding & Dropping</div>
                  <div className="prm-info-grid" style={{ marginBottom: 22 }}>
                    <div className="prm-info"><div className="l">Boarding Point</div><div className="v">{getSafeString(viewingBooking.boardingPoint.name)}</div></div>
                    <div className="prm-info"><div className="l">Boarding Time</div><div className="v">{getSafeString(viewingBooking.boardingPoint.time)}</div></div>
                    <div className="prm-info"><div className="l">Dropping Point</div><div className="v">{getSafeString(viewingBooking.droppingPoint.name)}</div></div>
                    <div className="prm-info"><div className="l">Dropping Time</div><div className="v">{getSafeString(viewingBooking.droppingPoint.time)}</div></div>
                  </div>
                </>
              )}

              {/* Passenger list */}
              {viewingBooking.passengers && viewingBooking.passengers.length > 0 && (
                <>
                  <div className="prm-section-title">Passenger List ({viewingBooking.passengers.length})</div>
                  <div className="prm-table-wrap">
                    <table className="prm-table">
                      <thead><tr><th>Name</th><th>Age</th><th>Gender</th><th>Seat</th></tr></thead>
                      <tbody>
                        {viewingBooking.passengers.map((p, i) => (
                          <tr key={i}>
                            <td><b>{p.name}</b></td>
                            <td>{p.age}</td>
                            <td>{p.gender}</td>
                            <td><span className="prm-tag navy">{p.seatNumber}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="prm-modal-foot">
              <button className="prm-btn primary" style={{ padding: '11px 28px' }} onClick={closeViewModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading details modal */}
      {viewingBooking && isFetchingDetails && (
        <div className="prm-overlay">
          <div className="prm-form-modal prm-form-modal-sm">
            <div className="prm-skel" style={{ padding: 40 }}>
              <div className="w40" /><div className="w60" /><div className="big" /><div /><div className="w60" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManage;